import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/authContext';
import useHttp from './useHttp';

// Encapsulates the Copago matching overview (which upgrades / price matrix
// entries map to which Copago article) and the price discrepancy report.
const useCopagoMatching = () => {
  const auth = useAuth();
  const { isLoading, error, sendRequest } = useHttp();

  const getMatching = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/copagoArticle/matching',
        method: 'GET',
        headers: auth.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Matching-Übersicht');
      throw err;
    }
  }, [auth, sendRequest]);

  const getPriceDiscrepancies = useCallback(
    async ({ recompute = false } = {}) => {
      try {
        return await sendRequest({
          url: `/api/v1/copagoArticle/priceDiscrepancies${recompute ? '?recompute=true' : ''}`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Preisdiskrepanzen');
        throw err;
      }
    },
    [auth, sendRequest]
  );

  // The suggestion run can take several minutes (longer than the HTTP gateway
  // timeout), so the backend starts a job and we poll its status until done.
  // onProgress receives { done, total } (targets) after each processed batch.
  const aiSuggestMatches = useCallback(
    async ({ onlyUnmatched = true, onProgress = null } = {}) => {
      const POLL_INTERVAL_MS = 3000;
      const POLL_TIMEOUT_MS = 8 * 60 * 1000;
      const MAX_CONSECUTIVE_POLL_ERRORS = 3;
      try {
        const started = await sendRequest({
          url: '/api/v1/copagoArticle/matching/ai',
          method: 'POST',
          headers: { ...auth.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { onlyUnmatched },
        });
        const jobId = started?.jobId;
        if (!jobId) throw new Error('Kein Job gestartet');

        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let consecutivePollErrors = 0;
        for (;;) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
          let job;
          try {
            job = await sendRequest({
              url: `/api/v1/copagoArticle/matching/ai/${jobId}`,
              method: 'GET',
              headers: auth.getAuthHeaders(),
            });
            consecutivePollErrors = 0;
          } catch (pollErr) {
            // The job keeps running server-side, so a single failed poll
            // (network blip) must not abort the whole run.
            consecutivePollErrors += 1;
            if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) throw pollErr;
            continue;
          }
          if (job?.progress && onProgress) onProgress(job.progress);
          if (job?.status === 'done') {
            return { suggestions: job.suggestions || [] };
          }
          if (job?.status === 'failed') {
            throw new Error(job.error || 'KI-Vorschläge fehlgeschlagen');
          }
          if (Date.now() > deadline) {
            throw new Error('Zeitüberschreitung beim Erzeugen der KI-Vorschläge');
          }
        }
      } catch (err) {
        toast.error(
          err?.message
            ? `Fehler beim Erzeugen der KI-Vorschläge: ${err.message}`
            : 'Fehler beim Erzeugen der KI-Vorschläge'
        );
        throw err;
      }
    },
    [auth, sendRequest]
  );

  const applyMatching = useCallback(
    async assignments => {
      try {
        return await sendRequest({
          url: '/api/v1/copagoArticle/matching/apply',
          method: 'POST',
          headers: { ...auth.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { assignments },
        });
      } catch (err) {
        toast.error('Fehler beim Speichern des Matchings');
        throw err;
      }
    },
    [auth, sendRequest]
  );

  return {
    isLoading,
    error,
    getMatching,
    getPriceDiscrepancies,
    aiSuggestMatches,
    applyMatching,
  };
};

export default useCopagoMatching;
